import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";

const PAGES = {
  privacy: {
    title: "Privacy Policy",
    description: "Privacy Policy for Anlytx for Elections (electionanlytx.com), operated by SevenM Technologies Private Limited.",
    intro:
      "This document describes the privacy practices of Election Anlytx. It is intended for website publication and should be read together with the Terms of Use and Disclaimer.",
    sections: [
      {
        title: "About This Privacy Policy",
        paras: [
          'This Privacy Policy explains how SevenM Technologies Private Limited ("SevenM", "we", "us" or "our"), operating the Election Anlytx platform ("Anlytx", "Platform"), collects, uses, stores, discloses and protects personal information when you visit or use electionanlytx.com and related services.',
          "Anlytx is an election-information and political analysis platform. The Platform does not require user accounts for ordinary access to its publicly available features.",
        ],
      },
      {
        title: "Information We Collect",
        paras: [
          "Information you provide: name, email address, organisation/professional details and other information you voluntarily provide through contact, feedback, enquiry or newsletter forms.",
          "Technical information: IP address, browser/device information, operating system, referring URL, pages viewed, access times, approximate location derived from technical information, diagnostic information and security logs.",
          "Analytics information: we use Google Analytics and may use other analytics or measurement technologies from time to time to understand website usage, performance and engagement.",
          "Payment/service information: if you purchase a paid Anlytx service, payment-related information may be processed by the applicable payment provider. We may receive transaction identifiers, payment status and limited billing information required to provide the service. We do not intend to store complete card credentials unless expressly stated for a specific service.",
        ],
      },
      {
        title: "How We Use Personal Information",
        paras: [],
        bullets: [
          "Provide, operate and improve Anlytx and its paid services.",
          "Respond to enquiries, feedback and support requests.",
          "Send newsletters, updates and marketing communications where permitted and where you have subscribed or otherwise provided the required permission.",
          "Measure website traffic, engagement and performance.",
          "Detect fraud, abuse, security incidents and unauthorised access.",
          "Maintain records, backups and technical logs.",
          "Comply with applicable legal, regulatory and governmental requirements.",
          "Protect the rights, property, security and integrity of Anlytx, SevenM and its users.",
        ],
      },
      {
        title: "Election and Public-Source Information",
        paras: [
          "Anlytx primarily uses election information published by the Election Commission of India (ECI) and may organise, transform, visualise or analyse such information.",
          "Election and political information may relate to candidates, political parties, constituencies, election results and other public electoral information. The publication or analysis of such information does not constitute endorsement or opposition to any candidate or political party.",
          "Public election information may be subject to corrections, updates, changes in presentation or differences between source datasets. Users should consult the ECI or other authoritative source when official confirmation is required.",
        ],
      },
      {
        title: "AI and Automated Processing",
        paras: [
          "Anlytx may use artificial intelligence, machine learning or automated processing to assist with data organisation, summaries, classification, visualisation or analytical presentation.",
          "Automated or AI-assisted outputs may contain errors or omissions. AI-generated or automated material should not be treated as an official statement of the Election Commission of India or of SevenM unless expressly identified as such.",
          "Where a matter is consequential, users should verify the underlying information against the relevant primary or official source.",
        ],
      },
      {
        title: "Cookies and Analytics",
        paras: [
          "Anlytx may use essential cookies and similar technologies required for website operation, as well as analytics technologies such as Google Analytics and other measurement services.",
          "Analytics information may be used in aggregated or pseudonymised form to understand traffic patterns, improve content and measure performance.",
          "You may control cookies through your browser settings. Disabling certain technologies may affect some website functionality. Where applicable law requires consent for non-essential cookies or tracking technologies, we will seek such consent.",
        ],
      },
      {
        title: "Newsletters and Marketing",
        paras: [
          "If you subscribe to an Anlytx newsletter or marketing communication, we may use your email address to send relevant communications about Anlytx, its services, research, updates or related information.",
          "You may unsubscribe from marketing communications at any time by using the unsubscribe mechanism in the communication or by contacting us at hello@electionanlytx.com.",
          "Transactional or service-related communications may still be sent where reasonably necessary to provide a service or comply with legal obligations.",
        ],
      },
      {
        title: "Sharing of Information",
        paras: [],
        bullets: [
          "Technology, hosting, analytics, payment, email, security and other service providers that process information on our behalf.",
          "Professional advisers, auditors and consultants where reasonably necessary.",
          "Government authorities, regulators, courts or law-enforcement agencies where required or permitted by law.",
          "Parties involved in a merger, acquisition, restructuring or transfer of relevant business assets, subject to applicable law.",
          "Other parties where you have authorised or requested the disclosure.",
        ],
      },
      {
        title: "Third-Party Services and Links",
        paras: [
          "Anlytx may link to or use third-party services, including Google Analytics, payment providers, hosting providers and other technology services. Third parties may process information according to their own terms and privacy policies.",
          "Anlytx is not responsible for the privacy practices, security or content of third-party websites that you may access through links on the Platform.",
        ],
      },
      {
        title: "Data Retention",
        paras: [
          "Personal information is retained only for as long as reasonably necessary for the purpose for which it was collected, or as required or permitted by applicable law.",
          "Retention may be longer where necessary to comply with legal obligations, resolve disputes, maintain security, establish or defend legal claims, or maintain legitimate business records.",
        ],
      },
      {
        title: "Data Security",
        paras: [
          "We use reasonable technical and organisational safeguards intended to protect personal information from unauthorised access, disclosure, alteration, loss, destruction or misuse.",
          "No method of internet transmission or electronic storage is completely secure. Accordingly, we cannot guarantee absolute security.",
        ],
      },
      {
        title: "Your Privacy Choices and Rights",
        paras: [
          "Subject to applicable law and the stage at which relevant provisions are in force, you may have rights concerning your personal data, including access to information about processing, correction, withdrawal of consent where consent is the basis of processing, erasure where applicable, and grievance redressal.",
          "Requests may be sent to hello@electionanlytx.com. We may need to verify your identity before acting on a request.",
          "Where consent is the basis of processing, withdrawing consent will not affect processing already carried out lawfully before withdrawal.",
        ],
      },
      {
        title: "Children",
        paras: [
          "Anlytx does not knowingly seek to collect personal information from children in circumstances where such collection is prohibited by applicable law. If you believe that personal information has been submitted inappropriately, please contact us.",
        ],
      },
      {
        title: "International Processing",
        paras: [
          "Some service providers used by Anlytx may process information outside India. Where personal information is transferred or processed outside India, SevenM will follow requirements applicable to such processing and transfers.",
        ],
      },
      {
        title: "Changes to This Privacy Policy",
        paras: [
          "We may update this Privacy Policy to reflect changes to Anlytx, technology, data practices or applicable law. The latest version published on electionanlytx.com will state its effective or last-updated date.",
        ],
      },
      {
        title: "Applicable Legal Framework",
        paras: [
          "This Privacy Policy is intended to operate with applicable Indian law, including the Digital Personal Data Protection Act, 2023, the Digital Personal Data Protection Rules, 2025, the Information Technology Act, 2000 and other applicable data-protection, privacy and technology requirements, to the extent applicable and in force from time to time.",
        ],
      },
      {
        title: "Contact",
        paras: ["For privacy questions, data requests or privacy-related concerns, contact hello@electionanlytx.com."],
      },
    ],
  },
  terms: {
    title: "Terms of Use",
    description: "Terms of Use for Anlytx for Elections (electionanlytx.com), operated by SevenM Technologies Private Limited.",
    intro:
      "These Terms govern use of Election Anlytx, including its public information platform and paid services.",
    sections: [
      {
        title: "Acceptance of Terms",
        paras: [
          'These Terms of Use ("Terms") govern access to and use of Election Anlytx ("Anlytx", "Platform"), operated by SevenM Technologies Private Limited.',
          "By accessing or using the Platform, you agree to these Terms. If you do not agree, you should discontinue use of the Platform.",
        ],
      },
      {
        title: "About Election Anlytx",
        paras: [
          "Anlytx is an election-information, data visualisation and political analysis platform.",
          "The Platform may provide constituency information, historical election data, election results, candidate and political-party information, charts, visualisations, analytical tools, research material and paid data or analytical services.",
          "Anlytx is not the Election Commission of India, a government department, political party, candidate, campaign organisation or official election authority.",
        ],
      },
      {
        title: "No User Accounts",
        paras: [
          "Anlytx does not currently provide user account registration for general Platform access.",
          "Certain paid services may require the collection of contact, billing or transaction information necessary to provide those services.",
        ],
      },
      {
        title: "Informational and Analytical Nature",
        paras: [
          "Information on Anlytx is provided for informational, educational, research and analytical purposes.",
          "The Platform is not intended to provide voting advice, recommend a candidate or political party, or determine how any person should vote.",
          "Users are responsible for their own political opinions, decisions and voting choices.",
        ],
      },
      {
        title: "Election Data",
        paras: [
          "Election information is primarily sourced from the Election Commission of India and may be processed, organised, visualised or analysed by Anlytx.",
          "Source information may change, be corrected or be updated. Users should consult the ECI or other authoritative source where official confirmation is required.",
        ],
      },
      {
        title: "AI-Assisted Analysis",
        paras: [
          "Anlytx may use artificial intelligence, machine learning and automated systems in parts of its analytical workflow.",
          "AI-assisted outputs may contain errors, omissions or contextual limitations and should not be treated as official ECI statements.",
          "Anlytx currently does not represent that its AI systems produce election predictions, seat projections or probability estimates. Any future analytical capability will remain subject to applicable terms and disclosures.",
        ],
      },
      {
        title: "Paid Services",
        paras: [
          "Anlytx may offer paid subscriptions, reports, data access, analytical services or other commercial offerings.",
          "Specific paid services may be subject to additional commercial terms, pricing, service descriptions, invoices, order forms or agreements.",
          "Prices, taxes, billing cycles, renewal terms, cancellation provisions and refund terms, where applicable, will be disclosed for the relevant service.",
          "Payment processing may be handled by third-party payment providers and may be subject to their terms.",
        ],
      },
      {
        title: "Political Neutrality",
        paras: [
          "Anlytx is designed to provide election information and analysis without endorsing, opposing or recommending any candidate, political party, political ideology or electoral choice.",
          "Political parties, candidates, campaign organisations, political consultants, journalists, researchers, businesses and other organisations may purchase or use paid Anlytx services where such services are offered to them.",
          "Providing such services does not constitute political endorsement.",
        ],
      },
      {
        title: "Acceptable Use",
        paras: [],
        bullets: [
          "Use the Platform only for lawful purposes.",
          "Do not attempt unauthorised access to Platform systems or security controls.",
          "Do not introduce malware, malicious code or harmful material.",
          "Do not interfere with Platform availability, integrity or security.",
          "Do not impersonate another person or misrepresent an affiliation with Anlytx, SevenM, ECI, a political party or candidate.",
          "Do not use Platform information to unlawfully harass, threaten or target individuals.",
          "Do not systematically scrape, reproduce, resell or redistribute Platform content in violation of applicable law, contractual restrictions or intellectual-property rights.",
          "Do not use the Platform to violate applicable election, privacy, copyright, data-protection or other laws.",
        ],
      },
      {
        title: "Intellectual Property",
        paras: [
          "Unless otherwise indicated, the Anlytx software, website design, branding, original text, graphics, visualisations, compilation, databases and analytical presentation are owned by or licensed to SevenM Technologies Private Limited or its licensors.",
          "Nothing in these Terms transfers ownership of such intellectual property.",
          "Lawful personal, educational and research use of publicly available information is permitted subject to these Terms and applicable law. Commercial reproduction, systematic redistribution, resale or creation of a competing database/service may require prior written permission.",
        ],
      },
      {
        title: "Feedback and Contact Forms",
        paras: [
          "Users may submit feedback, corrections or enquiries through designated forms.",
          "Users should not submit confidential information, unlawful material, another person's personal information without appropriate authority, or material that infringes third-party rights.",
          "By submitting feedback or corrections, you permit Anlytx to review and use the submission for purposes reasonably connected with operating, improving or correcting the Platform, subject to applicable law and the Privacy Policy.",
        ],
      },
      {
        title: "Accuracy, Corrections and Sources",
        paras: [
          "Anlytx makes reasonable efforts to present useful and reliable information but does not guarantee that all content is complete, current or error-free.",
          "If you identify an apparent error, you may contact hello@electionanlytx.com with the relevant page, issue and supporting source. We may review and update information where appropriate.",
          "A correction request does not guarantee that a particular change will be made.",
        ],
      },
      {
        title: "Availability",
        paras: [
          "Anlytx may modify, suspend, discontinue or restrict any part of the Platform or a paid service where reasonably necessary.",
          "We do not guarantee uninterrupted availability, continuous access, error-free operation or continued availability of any particular dataset or feature.",
        ],
      },
      {
        title: "Disclaimer of Warranties",
        paras: [
          "To the maximum extent permitted by applicable law, the Platform and its content are provided on an 'as is' and 'as available' basis.",
          "We do not warrant that information will always be accurate, complete, current, uninterrupted, secure or suitable for a particular purpose.",
        ],
      },
      {
        title: "Limitation of Liability",
        paras: [
          "To the maximum extent permitted by applicable law, SevenM Technologies Private Limited and its officers, employees, contractors, licensors and service providers will not be liable for indirect, incidental, consequential, special or punitive losses arising from or related to use of the Platform.",
          "Nothing in these Terms excludes or limits liability that cannot lawfully be excluded or limited.",
        ],
      },
      {
        title: "Indemnification",
        paras: [
          "To the extent permitted by law, you agree to indemnify and hold harmless SevenM Technologies Private Limited and its officers, employees, contractors and service providers from claims, losses, liabilities, damages and expenses arising from your unlawful use of the Platform, violation of these Terms, infringement of third-party rights or material you submit.",
        ],
      },
      {
        title: "Changes and Termination",
        paras: [
          "We may update these Terms from time to time. Continued use after the effective date of revised Terms constitutes acceptance to the extent permitted by law.",
          "We may suspend or terminate access where reasonably necessary to protect the Platform, address security or abuse, comply with law or enforce these Terms.",
        ],
      },
      {
        title: "Governing Law and Jurisdiction",
        paras: [
          "These Terms are governed by the laws of India.",
          "Subject to applicable law, courts having jurisdiction in Kolkata, West Bengal, India shall have jurisdiction over disputes arising from or relating to these Terms.",
        ],
      },
      {
        title: "Contact",
        paras: ["Questions regarding these Terms may be sent to hello@electionanlytx.com."],
      },
    ],
  },
  disclaimer: {
    title: "Disclaimer",
    description: "Disclaimer for Anlytx for Elections (electionanlytx.com), operated by SevenM Technologies Private Limited.",
    intro:
      "Election Anlytx provides election information and analysis. This Disclaimer explains the limits, sources and non-endorsement nature of that information.",
    sections: [
      {
        title: "General Disclaimer",
        paras: [
          "Election Anlytx is an independent election-information and political analysis platform operated by SevenM Technologies Private Limited.",
          "Information on the Platform is provided for general informational, educational, research and analytical purposes.",
        ],
      },
      {
        title: "Not an Official Election Authority",
        paras: [
          "Anlytx is not the Election Commission of India, a State Election Commission, a government department or an official election-results portal.",
          "Anlytx is also not a political party, candidate, candidate campaign, political organisation or official representative of any political organisation.",
          "The presence of information about an election, constituency, candidate or political party on Anlytx does not imply official affiliation or endorsement.",
        ],
      },
      {
        title: "No Voting Advice or Political Recommendation",
        paras: [
          "Anlytx does not tell users whom to vote for or against.",
          "Charts, statistics, historical results, comparisons, profiles and analytical observations are intended to help users understand election-related information. Users make their own political decisions.",
        ],
      },
      {
        title: "Election Data",
        paras: [
          "Election data presented by Anlytx is primarily sourced from the Election Commission of India and may be processed, organised, visualised or analysed for presentation.",
          "Source data may be updated, corrected or revised. Users should refer to the ECI or other authoritative source where official confirmation is required.",
        ],
      },
      {
        title: "AI-Assisted Analysis",
        paras: [
          "Parts of Anlytx may use artificial intelligence, machine learning or automated processing.",
          "AI-assisted material may contain inaccuracies, omissions or contextual limitations. It should not automatically be treated as factual or official merely because it is generated or presented by a computer system.",
          "Anlytx does not currently provide election predictions, seat projections or probability estimates as a stated Platform feature.",
        ],
      },
      {
        title: "No Guarantee of Future Election Outcomes",
        paras: [
          "Historical election results, trends or analytical observations do not determine future election outcomes.",
          "Where future-oriented analytical features are introduced, any such output will be inherently uncertain and should not be treated as a guarantee, certainty or official statement.",
        ],
      },
      {
        title: "Accuracy and Completeness",
        paras: [
          "Reasonable efforts are made to present useful and reliable information. However, no representation is made that every item is complete, current, accurate or error-free.",
          "Information may contain delays, omissions, corrections, differences in methodology or changes after publication.",
        ],
      },
      {
        title: "Third-Party Sources",
        paras: [
          "Anlytx may reference or link to the Election Commission of India and other third-party sources. Anlytx does not control third-party websites and is not responsible for their content, availability, security or privacy practices.",
        ],
      },
      {
        title: "No Professional Advice",
        paras: [
          "Anlytx content does not constitute legal advice, financial advice, investment advice, electoral advice or other professional advice.",
          "Users should obtain appropriate professional or official advice where required.",
        ],
      },
      {
        title: "Commercial and Political Users",
        paras: [
          "Political parties, candidates, campaign organisations, political consultants, journalists, researchers, businesses and other organisations may use or purchase Anlytx services where offered.",
          "Providing access to a paid service does not constitute endorsement, opposition or political support.",
        ],
      },
      {
        title: "External Links",
        paras: [
          "External links are provided for convenience or source reference. Users access external websites at their own risk and subject to those websites' own terms and policies.",
        ],
      },
      {
        title: "Limitation",
        paras: [
          "To the maximum extent permitted by applicable law, SevenM Technologies Private Limited disclaims liability for losses arising from reliance on information contained on the Platform, except to the extent such liability cannot lawfully be excluded.",
        ],
      },
      {
        title: "Changes",
        paras: [
          "This Disclaimer may be updated as the Platform, its data sources, analytical capabilities or applicable legal requirements change. The latest version published on electionanlytx.com will apply from its stated effective date.",
        ],
      },
      {
        title: "Contact",
        paras: ["For questions regarding this Disclaimer, contact hello@electionanlytx.com."],
      },
    ],
  },
};

const Legal = ({ page }) => {
  const content = PAGES[page] || PAGES.privacy;
  return (
    <div data-testid={`legal-${page}-page`}>
      <Seo title={`${content.title} | Anlytx for Elections`} description={content.description} />
      <section className="bg-[#0b1322]">
        <div className="mx-auto max-w-4xl px-6 py-16 text-center sm:py-20">
          <Reveal>
            <p className="font-mono-data text-xs font-medium uppercase tracking-[0.24em] text-red-400">Legal</p>
            <h1 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              {content.title}
            </h1>
            <p className="mt-5 inline-block rounded-full border border-white/15 px-4 py-1.5 font-mono-data text-[10px] uppercase tracking-[0.2em] text-slate-300">
              Effective 01 September 2026 &bull; electionanlytx.com
            </p>
          </Reveal>
        </div>
      </section>
      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-6 py-14">
          <Reveal>
            <p className="text-base leading-relaxed text-slate-600">{content.intro}</p>
          </Reveal>
          {content.sections.map((s, i) => (
            <Reveal key={s.title}>
              <article
                className="grid gap-4 border-t border-slate-200 py-10 md:grid-cols-[64px_1fr]"
                data-testid={`legal-section-${i + 1}`}
              >
                <span className="font-mono-data text-2xl font-bold tracking-tight text-[#b91c1c]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">{s.title}</h2>
                  <div className="mt-3 space-y-3">
                    {s.paras.map((p, j) => (
                      <p key={j} className="text-sm leading-relaxed text-slate-600">
                        {p}
                      </p>
                    ))}
                  </div>
                  {s.bullets && (
                    <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-slate-600">
                      {s.bullets.map((b, j) => (
                        <li key={j}>{b}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
          <Reveal>
            <div className="mt-4 rounded-lg border border-slate-200 bg-[#f8fafc] p-6">
              <p className="font-heading text-sm font-semibold text-slate-900">SevenM Technologies Private Limited</p>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">
                508, 5th Floor, Saltee Plaza, 1 Khudiram Bose Sarani, Kolkata 700080, West Bengal, India
              </p>
              <a
                href="mailto:hello@electionanlytx.com"
                data-testid="legal-contact-email"
                className="mt-2 inline-block text-sm font-semibold text-[#b91c1c] transition-colors hover:text-[#991b1b]"
              >
                hello@electionanlytx.com
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Legal;
